import React from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, Trash2 } from 'lucide-react';
import type { VocabWord } from 'modules/vocabulary';
import { MarkedContext } from './WordRow';
import { LearnedToggle, StatusChip, sourceLabel } from './WordStatus';

/** The word's translation in the interface language, or '' until it has a definition. */
export function translationOf(item: VocabWord, language: string): string {
  const def = item.definition;
  if (!def) return '';
  return language.startsWith('ru') ? def.ru : def.uz;
}

/** One saved word as a phone card: the word, its translation, and the sentence it came from with the word marked. */
interface WordCardProps {
  item: VocabWord;
  locked: boolean;
  onDelete: () => void;
  onToggleLearned: () => void;
}

export function WordCard({ item, locked, onDelete, onToggleLearned }: WordCardProps) {
  const { t, i18n } = useTranslation();
  const def = item.definition;
  const translation = translationOf(item, i18n.language);
  const example = def?.example || item.context;

  return (
    <li className="rounded-[18px] border border-border bg-card p-4">
      <div className="flex items-start justify-between gap-2.5">
        <div className="min-w-0">
          <p className="flex flex-wrap items-baseline gap-x-2">
            <span className="break-words font-display text-[19px] font-extrabold tracking-[-0.03em] text-foreground">{item.word}</span>
            {def?.partOfSpeech && <span className="text-xs italic text-muted-foreground">{def.partOfSpeech}</span>}
          </p>
          {def ? (
            <p className="mt-0.5 text-sm text-foreground/80">
              {translation}
              {def.meaning && <span className="block text-[13px] text-muted-foreground">{def.meaning}</span>}
            </p>
          ) : locked ? (
            <p className="mt-1 inline-flex items-center gap-1.5 text-[13px] text-muted-foreground">
              <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden /> {t('vocab.definition_locked')}
            </p>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <StatusChip learned={item.learned === true} />
          <button
            type="button"
            onClick={onDelete}
            aria-label={t('vocab.delete', { word: item.word })}
            className="-my-2 -mr-2 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Trash2 className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </div>
      {example && (
        <p className="mt-2.5 text-[13.5px] leading-[1.55] text-muted-foreground">
          <MarkedContext context={example} word={item.word} markClass="bg-[#F4FFB8] font-bold dark:bg-primary/30" />
        </p>
      )}
      <div className="mt-3 flex items-center justify-between gap-3 text-xs text-muted-foreground">
        <span className="min-w-0 break-words">{sourceLabel(item)}</span>
        <LearnedToggle item={item} onToggle={onToggleLearned} variant="text" />
      </div>
    </li>
  );
}

export default WordCard;
